<template>
  <div class="d-inline-block">
    <b-button size="sm" variant="outline-primary" @click="openCreateModal()" class="mr-1">
      Create ABHA
    </b-button>
    <b-button size="sm" variant="outline-success" @click="openVerifyModal()">
      Verify ABHA
    </b-button>

    <!-- ============================================================ -->
    <!-- CREATE ABHA Modal (Enrollment via Aadhaar)                    -->
    <!-- ============================================================ -->
    <b-modal
      v-model="createDialog"
      hide-footer
      centered
      title="Create ABHA via Aadhaar"
      size="lg"
      no-close-on-backdrop
      @hidden="onModalHidden()"
    >
      <!-- Step C0: NHA Consent Collection -->
      <div v-if="createStep === 'consent'">
        <h6 class="mb-2">Consent for ABHA Creation</h6>
        <p class="text-muted small mb-2">
          Please read and accept the following consent as per ABDM / National Health Authority guidelines.
        </p>
        <div class="border rounded p-3 bg-light mb-3" style="max-height: 350px; overflow-y: auto; font-size: 13px;">
          <p class="mb-1"><strong>I hereby declare that:</strong></p>

          <b-form-checkbox v-model="consent.aadhaar_sharing" class="mb-2">
            I am voluntarily sharing my Aadhaar Number / Virtual ID issued by the Unique Identification
            Authority of India ("UIDAI"), and my demographic information for the purpose of creating an
            Ayushman Bharat Health Account number ("ABHA number") and Ayushman Bharat Health Account
            address ("ABHA Address"). I authorize NHA to use my Aadhaar number / Virtual ID for performing
            Aadhaar based authentication with UIDAI as per the provisions of the Aadhaar (Targeted Delivery
            of Financial and other Subsidies, Benefits and Services) Act, 2016 for the aforesaid purpose.
            I understand that UIDAI will share my e-KYC details, or response of "Yes" with NHA upon
            successful authentication.
          </b-form-checkbox>

          <b-form-checkbox v-model="consent.legacy_records" class="mb-2">
            I consent to usage of my ABHA address and ABHA number for linking of my legacy (past)
            government health records and those which will be generated during this encounter.
          </b-form-checkbox>

          <b-form-checkbox v-model="consent.health_records" class="mb-2">
            I authorize the sharing of all my health records with healthcare provider(s) for the
            purpose of providing healthcare services to me during this encounter.
          </b-form-checkbox>

          <b-form-checkbox v-model="consent.anonymization" class="mb-2">
            I consent to the anonymization and subsequent use of my government health records for
            public health purposes.
          </b-form-checkbox>

          <hr class="my-2" />

          <b-form-checkbox v-model="consent.worker_confirm" class="mb-2">
            I, <strong>{{ workerName || '(Healthcare Worker)' }}</strong>, confirm that I have duly
            informed and explained the beneficiary of the contents of consent for aforementioned purposes.
          </b-form-checkbox>

          <b-form-checkbox v-model="consent.beneficiary_confirm" class="mb-2">
            I, <strong>{{ beneficiaryName || '(Beneficiary)' }}</strong>, have been explained about
            the consent as stated above and hereby provide my consent for the aforementioned purposes.
          </b-form-checkbox>
        </div>

        <b-alert v-if="consentError" show variant="danger" class="py-1 px-2 mb-2">
          <small>{{ consentError }}</small>
        </b-alert>

        <div class="d-flex justify-content-end">
          <b-button variant="text" class="text-danger mr-2" @click="createDialog = false">
            Cancel
          </b-button>
          <b-button variant="primary" @click="validateConsent('create')">
            I Agree &amp; Proceed
          </b-button>
        </div>
      </div>

      <!-- Step C1: Choose verification method -->
      <div v-if="createStep === 'method'">
        <p class="mb-2">
          <strong>Aadhaar Number:</strong> {{ maskedAadhaar }}
        </p>
        <p class="text-muted small mb-3">Choose a verification method to create your ABHA:</p>
        <div class="d-flex flex-column">
          <b-button variant="primary" class="mb-2" :disabled="createLoading" @click="enrollmentRequestOtp()">
            <b-spinner small v-if="createLoading && createMethod === 'otp'" class="mr-1"></b-spinner>
            <i class="mdi mdi-message-text mr-1" v-else></i>
            Verify with OTP
          </b-button>
          <!-- Fingerprint hidden for now, enable later -->
          <!-- <b-button variant="info" class="mb-2" :disabled="createLoading" @click="enrollByFingerprint()">
            <b-spinner small v-if="createLoading && createMethod === 'fingerprint'" class="mr-1"></b-spinner>
            <i class="mdi mdi-fingerprint mr-1" v-else></i>
            Verify with Fingerprint
          </b-button> -->
          <b-button variant="secondary" class="mb-2" :disabled="createLoading" @click="startFaceAuth('create')">
            <i class="mdi mdi-camera mr-1"></i>
            Verify with Face
          </b-button>
          <b-button variant="warning" class="mb-2" :disabled="createLoading" @click="createStep = 'demo_auth'">
            <i class="mdi mdi-account-details mr-1"></i>
            Verify with Demographics (Demo Auth)
          </b-button>
        </div>
        <div class="d-flex justify-content-end mt-2">
          <b-button variant="text" class="text-danger" @click="createDialog = false">Cancel</b-button>
        </div>
      </div>

      <!-- Step C2: Enter OTP -->
      <div v-if="createStep === 'otp'">
        <p class="text-muted small mb-2">
          <template v-if="createMaskedMobile">
            We just sent an OTP on the Mobile Number <strong>{{ createMaskedMobile }}</strong>
            linked with Aadhaar. Enter the OTP below to proceed with ABHA creation.
          </template>
          <template v-else>
            Enter the 6-digit OTP sent to your Aadhaar-linked mobile number.
          </template>
        </p>
        <form-wrapper @submit="enrollmentVerifyOtp()">
          <input-field
            label="Enter 6-digit OTP"
            type="number"
            rules="required"
            v-model="createOtp"
          ></input-field>
          <input-field
            label="Communication Mobile Number"
            type="number"
            rules="required"
            v-model="createMobile"
          ></input-field>

          <!-- Resend OTP -->
          <div class="mb-3">
            <b-button
              variant="link"
              size="sm"
              :disabled="resendTimer > 0 || resendCount >= 2"
              @click="resendCreateOtp()"
              class="p-0"
            >
              <template v-if="resendTimer > 0">Resend OTP in {{ resendTimer }}s</template>
              <template v-else-if="resendCount >= 2">Maximum resend attempts reached</template>
              <template v-else>Resend OTP</template>
            </b-button>
            <small v-if="resendCount > 0 && resendCount < 2" class="text-muted ml-2">
              ({{ 2 - resendCount }} resend{{ 2 - resendCount > 1 ? 's' : '' }} remaining)
            </small>
          </div>

          <div class="d-flex justify-content-end mt-3">
            <b-button variant="text" class="text-danger mr-2" @click="createDialog = false">Cancel</b-button>
            <b-button variant="primary" type="submit" :disabled="createLoading">
              <b-spinner small v-if="createLoading" class="mr-1"></b-spinner>
              Verify &amp; Create ABHA
            </b-button>
          </div>
        </form-wrapper>
      </div>

      <!-- Step C2F: Face Auth (QR-based) -->
      <div v-if="createStep === 'face'">
        <div v-if="!faceAuthQrDataUrl && !faceAuthStatus">
          <p class="text-muted small mb-2">Initializing face authentication...</p>
          <div class="text-center"><b-spinner></b-spinner></div>
        </div>

        <div v-else-if="faceAuthStatus === 'COMPLETE'">
          <b-alert variant="success" show>
            <i class="mdi mdi-check-circle mr-1"></i>
            Face authentication completed successfully! Enrolling...
          </b-alert>
          <div class="text-center"><b-spinner small></b-spinner></div>
        </div>

        <div v-else-if="faceAuthStatus === 'FAILED'">
          <b-alert variant="danger" show>
            <i class="mdi mdi-alert-circle mr-1"></i>
            Face authentication failed. Please try again.
          </b-alert>
          <b-button variant="primary" size="sm" @click="startFaceAuth('create')">
            <i class="mdi mdi-refresh mr-1"></i> Retry
          </b-button>
        </div>

        <div v-else>
          <p class="text-muted small mb-2">
            Scan this QR code with the <strong>ABHA mobile app</strong> to complete face verification.
          </p>
          <div class="text-center mb-3">
            <img :src="faceAuthQrDataUrl" alt="Face Auth QR Code" class="border rounded" style="width: 250px; height: 250px;" />
          </div>
          <p class="text-center small">
            <span v-if="faceAuthStatus === 'PENDING'" class="text-warning">
              <b-spinner small class="mr-1"></b-spinner> Waiting for face capture...
            </span>
            <span v-else-if="faceAuthStatus === 'VERIFIED'" class="text-info">
              <b-spinner small class="mr-1"></b-spinner> Face verified, completing authentication...
            </span>
            <span v-else class="text-muted">
              <b-spinner small class="mr-1"></b-spinner> Waiting for QR scan...
            </span>
          </p>
        </div>

        <div class="d-flex justify-content-end mt-2">
          <b-button variant="text" class="text-danger" @click="cancelFaceAuth(); createStep = 'method'">Back</b-button>
        </div>
      </div>

      <!-- Step C2D: Demo Auth -->
      <div v-if="createStep === 'demo_auth'">
        <p class="text-muted small mb-2">Verify your identity using demographic details as per Aadhaar.</p>
        <form-wrapper @submit="submitDemoAuth()">
          <input-field
            label="Full Name (as per Aadhaar)"
            rules="required"
            v-model="demoName"
          ></input-field>
          <b-form-group label="Gender" label-size="sm">
            <b-form-select v-model="demoGender" :options="[
              { value: '', text: 'Select Gender' },
              { value: 'M', text: 'Male' },
              { value: 'F', text: 'Female' },
              { value: 'O', text: 'Other' },
            ]" required size="sm"></b-form-select>
          </b-form-group>
          <input-field
            label="Date of Birth"
            type="date"
            rules="required"
            v-model="demoDob"
          ></input-field>
          <input-field
            label="Mobile Number"
            type="number"
            rules="required"
            v-model="demoMobile"
          ></input-field>
          <input-field
            label="State Code (LGD)"
            type="number"
            rules="required"
            v-model="demoStateCode"
          ></input-field>
          <input-field
            label="District Code (LGD)"
            type="number"
            rules="required"
            v-model="demoDistrictCode"
          ></input-field>
          <input-field
            label="Address"
            v-model="demoAddress"
          ></input-field>
          <input-field
            label="Pincode"
            type="number"
            v-model="demoPincode"
          ></input-field>
          <div class="d-flex justify-content-end mt-3">
            <b-button variant="text" class="text-danger mr-2" @click="createStep = 'method'">Back</b-button>
            <b-button variant="primary" type="submit" :disabled="createLoading">
              <b-spinner small v-if="createLoading" class="mr-1"></b-spinner>
              Verify &amp; Create ABHA
            </b-button>
          </div>
        </form-wrapper>
      </div>

      <!-- Step C3: Communication Mobile Verification -->
      <div v-if="createStep === 'mobile_verify'">
        <b-alert show variant="info" class="py-2 px-3">
          <template v-if="aadhaarLinkedMobile || createMaskedMobile">
            The communication mobile number <strong>{{ commMobile }}</strong> does not match
            the Aadhaar-linked mobile ending with <strong>{{ (aadhaarLinkedMobile || createMaskedMobile).slice(-4) }}</strong>.
          </template>
          <template v-else>
            The communication mobile number <strong>{{ commMobile }}</strong> needs to be verified.
          </template>
          Please verify this mobile number with an OTP before proceeding.
        </b-alert>
        <form-wrapper @submit="verifyCommMobileOtp()">
          <p class="mb-2"><strong>Communication Mobile:</strong> {{ commMobile }}</p>
          <div v-if="!commMobileOtpSent">
            <b-button variant="primary" :disabled="createLoading" @click="sendCommMobileOtp()">
              <b-spinner small v-if="createLoading" class="mr-1"></b-spinner>
              Send OTP to {{ commMobile }}
            </b-button>
          </div>
          <div v-else>
            <input-field
              label="Enter OTP sent to communication mobile"
              type="number"
              rules="required"
              v-model="commMobileOtp"
            ></input-field>

            <!-- Resend OTP for mobile verify -->
            <div class="mb-3">
              <b-button
                variant="link"
                size="sm"
                :disabled="commResendTimer > 0 || commResendCount >= 2"
                @click="resendCommMobileOtp()"
                class="p-0"
              >
                <template v-if="commResendTimer > 0">Resend OTP in {{ commResendTimer }}s</template>
                <template v-else-if="commResendCount >= 2">Maximum resend attempts reached</template>
                <template v-else>Resend OTP</template>
              </b-button>
            </div>

            <div class="d-flex justify-content-end mt-3">
              <b-button variant="text" class="text-danger mr-2" @click="createDialog = false">Cancel</b-button>
              <b-button variant="primary" type="submit" :disabled="createLoading">
                <b-spinner small v-if="createLoading" class="mr-1"></b-spinner>
                Verify Mobile
              </b-button>
            </div>
          </div>
        </form-wrapper>
      </div>

      <!-- Step C4: ABHA Address Selection -->
      <div v-if="createStep === 'address'">
        <h6 class="mb-2">Choose or Create ABHA Address</h6>
        <p class="text-muted small mb-2">
          Select a suggested ABHA address or create a custom one.
        </p>

        <div v-if="addressSuggestions.length > 0" class="mb-3">
          <b-form-group label="Suggested ABHA Addresses:">
            <b-form-radio-group v-model="selectedAddress" stacked>
              <b-form-radio
                v-for="(addr, idx) in addressSuggestions"
                :key="idx"
                :value="addr"
                class="mb-1"
              >
                {{ addr }}@{{ cmSuffix }}
              </b-form-radio>
              <b-form-radio value="__custom__" class="mb-1">
                Create custom ABHA address
              </b-form-radio>
            </b-form-radio-group>
          </b-form-group>
        </div>

        <div v-if="selectedAddress === '__custom__' || addressSuggestions.length === 0">
          <input-field
            label="Custom ABHA Address"
            v-model="customAddress"
            rules="required"
            :placeholder="'e.g., yourname123'"
          ></input-field>
          <small class="text-muted d-block mb-2">
            Rules: 8-18 characters, alphanumeric, max 1 dot (.) and 1 underscore (_),
            special characters cannot be at start or end.
          </small>
          <b-alert v-if="addressError" show variant="danger" class="py-1 px-2">
            <small>{{ addressError }}</small>
          </b-alert>
        </div>

        <div class="d-flex justify-content-end mt-3">
          <b-button variant="text" class="text-danger mr-2" @click="createDialog = false">Cancel</b-button>
          <b-button variant="primary" :disabled="createLoading" @click="submitAbhaAddress()">
            <b-spinner small v-if="createLoading" class="mr-1"></b-spinner>
            Create ABHA Address
          </b-button>
        </div>
      </div>

      <!-- Step C5: ABHA Created - Profile + Card -->
      <div v-if="createStep === 'success'">
        <b-alert show :variant="isExistingAccount ? 'info' : 'success'" class="py-2 px-3">
          <strong>{{ isExistingAccount ? 'Existing ABHA Account Retrieved Successfully' : 'New ABHA Account Created Successfully!' }}</strong>
          <span v-if="isExistingAccount" class="d-block small mt-1">An existing ABHA account was found for this Aadhaar. You can view the details below and link it to the recipient.</span>
          <span v-else class="d-block small mt-1">A new ABHA account has been created. You can view and download the ABHA card below, then link it to the recipient.</span>
        </b-alert>
        <div v-if="abhaProfile" class="mt-2">
          <table class="table table-sm table-bordered mb-2">
            <tr>
              <td class="font-weight-bold" width="40%">ABHA Number</td>
              <td>{{ abhaProfile.ABHANumber || abhaProfile.abhaNumber }}</td>
            </tr>
            <tr>
              <td class="font-weight-bold">Name</td>
              <td>{{ abhaProfile.firstName }} {{ abhaProfile.middleName }} {{ abhaProfile.lastName }}</td>
            </tr>
            <tr v-if="resolvedAbhaAddress">
              <td class="font-weight-bold">ABHA Address</td>
              <td>{{ resolvedAbhaAddress }}</td>
            </tr>
            <tr v-if="abhaProfile.mobile">
              <td class="font-weight-bold">Mobile</td>
              <td>{{ abhaProfile.mobile }}</td>
            </tr>
            <tr v-if="abhaProfile.gender">
              <td class="font-weight-bold">Gender</td>
              <td>{{ abhaProfile.gender }}</td>
            </tr>
            <tr v-if="abhaProfile.dob">
              <td class="font-weight-bold">Date of Birth</td>
              <td>{{ abhaProfile.dob }}</td>
            </tr>
          </table>
        </div>

        <!-- ABHA Card -->
        <div class="mb-3">
          <b-button variant="outline-info" size="sm" class="mr-1" :disabled="cardLoading" @click="viewAbhaCard('create')">
            <b-spinner small v-if="cardLoading" class="mr-1"></b-spinner>
            View ABHA Card
          </b-button>
          <b-button variant="outline-secondary" size="sm" :disabled="cardLoading" @click="downloadAbhaCard('create')">
            Download ABHA Card (PDF)
          </b-button>
        </div>
        <div v-if="abhaCardBase64" class="text-center mb-3">
          <img :src="'data:image/png;base64,' + abhaCardBase64" class="border rounded" style="max-width: 100%;" />
        </div>

        <div class="d-flex justify-content-end mt-3">
          <b-button variant="primary" :disabled="linkLoading" @click="applyAbhaProfile()">
            <b-spinner small v-if="linkLoading" class="mr-1"></b-spinner>
            Link to Recipient
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- ============================================================ -->
    <!-- VERIFY ABHA Modal (Login with existing ABHA)                  -->
    <!-- ============================================================ -->
    <b-modal
      v-model="verifyDialog"
      hide-footer
      centered
      title="Verify Existing ABHA"
      size="lg"
      no-close-on-backdrop
      @hidden="onModalHidden()"
    >
      <!-- Step V1: Search - Choose identifier type -->
      <div v-if="verifyStep === 'search'">
        <b-tabs v-model="verifyTabIndex" content-class="mt-3" small>
          <!-- Tab 1: ABHA Number / Address -->
          <b-tab title="ABHA Number / Address">
            <form-wrapper @submit="onVerifySearchSubmit()">
              <input-field
                label="ABHA Number or ABHA Address"
                rules="required"
                v-model="verifyAbhaInput"
              >
                <template v-slot:help>
                  <help text="Enter 14-digit ABHA number (e.g., 91-1234-5678-9012) or ABHA address (e.g., user@abdm)"></help>
                </template>
              </input-field>
              <div class="d-flex justify-content-end mt-3">
                <b-button variant="text" class="text-danger mr-2" @click="verifyDialog = false">Cancel</b-button>
                <b-button variant="primary" type="submit" :disabled="verifyLoading">
                  <b-spinner small v-if="verifyLoading" class="mr-1"></b-spinner>
                  Proceed
                </b-button>
              </div>
            </form-wrapper>
          </b-tab>

          <!-- Tab 2: Mobile Number -->
          <b-tab title="Verify by Mobile">
            <form-wrapper @submit="onVerifyMobileSearch()">
              <input-field
                label="Mobile Number"
                type="number"
                rules="required"
                v-model="verifyMobileInput"
                placeholder="Enter 10-digit mobile number"
              ></input-field>
              <div class="d-flex justify-content-end mt-3">
                <b-button variant="text" class="text-danger mr-2" @click="verifyDialog = false">Cancel</b-button>
                <b-button variant="primary" type="submit" :disabled="verifyLoading">
                  <b-spinner small v-if="verifyLoading" class="mr-1"></b-spinner>
                  Search
                </b-button>
              </div>
            </form-wrapper>
          </b-tab>

          <!-- Tab 3: Aadhaar Number -->
          <b-tab title="Verify by Aadhaar">
            <form-wrapper @submit="onVerifyAadhaarSearch()">
              <input-field
                label="Aadhaar Number"
                type="number"
                rules="required"
                v-model="verifyAadhaarInput"
                placeholder="Enter 12-digit Aadhaar number"
              ></input-field>
              <div class="d-flex justify-content-end mt-3">
                <b-button variant="text" class="text-danger mr-2" @click="verifyDialog = false">Cancel</b-button>
                <b-button variant="primary" type="submit" :disabled="verifyLoading">
                  <b-spinner small v-if="verifyLoading" class="mr-1"></b-spinner>
                  Search
                </b-button>
              </div>
            </form-wrapper>
          </b-tab>
        </b-tabs>
      </div>

      <!-- Step V1C: Consent (same as create) -->
      <div v-if="verifyStep === 'consent'">
        <h6 class="mb-2">Consent for ABHA Verification</h6>
        <p class="text-muted small mb-2">
          Please read and accept the following consent as per ABDM / National Health Authority guidelines.
        </p>
        <div class="border rounded p-3 bg-light mb-3" style="max-height: 350px; overflow-y: auto; font-size: 13px;">
          <p class="mb-1"><strong>I hereby declare that:</strong></p>

          <b-form-checkbox v-model="consent.aadhaar_sharing" class="mb-2">
            I am voluntarily sharing my Aadhaar Number / Virtual ID issued by the Unique Identification
            Authority of India ("UIDAI"), and my demographic information for the purpose of creating an
            Ayushman Bharat Health Account number ("ABHA number") and Ayushman Bharat Health Account
            address ("ABHA Address"). I authorize NHA to use my Aadhaar number / Virtual ID for performing
            Aadhaar based authentication with UIDAI as per the provisions of the Aadhaar (Targeted Delivery
            of Financial and other Subsidies, Benefits and Services) Act, 2016 for the aforesaid purpose.
            I understand that UIDAI will share my e-KYC details, or response of "Yes" with NHA upon
            successful authentication.
          </b-form-checkbox>

          <b-form-checkbox v-model="consent.legacy_records" class="mb-2">
            I consent to usage of my ABHA address and ABHA number for linking of my legacy (past)
            government health records and those which will be generated during this encounter.
          </b-form-checkbox>

          <b-form-checkbox v-model="consent.health_records" class="mb-2">
            I authorize the sharing of all my health records with healthcare provider(s) for the
            purpose of providing healthcare services to me during this encounter.
          </b-form-checkbox>

          <b-form-checkbox v-model="consent.anonymization" class="mb-2">
            I consent to the anonymization and subsequent use of my government health records for
            public health purposes.
          </b-form-checkbox>

          <hr class="my-2" />

          <b-form-checkbox v-model="consent.worker_confirm" class="mb-2">
            I, <strong>{{ workerName || '(Healthcare Worker)' }}</strong>, confirm that I have duly
            informed and explained the beneficiary of the contents of consent for aforementioned purposes.
          </b-form-checkbox>

          <b-form-checkbox v-model="consent.beneficiary_confirm" class="mb-2">
            I, <strong>{{ beneficiaryName || '(Beneficiary)' }}</strong>, have been explained about
            the consent as stated above and hereby provide my consent for the aforementioned purposes.
          </b-form-checkbox>
        </div>

        <b-alert v-if="consentError" show variant="danger" class="py-1 px-2 mb-2">
          <small>{{ consentError }}</small>
        </b-alert>

        <div class="d-flex justify-content-end">
          <b-button variant="text" class="text-danger mr-2" @click="verifyDialog = false">Cancel</b-button>
          <b-button variant="primary" @click="validateConsent('verify')">
            I Agree &amp; Proceed
          </b-button>
        </div>
      </div>

      <!-- Step V2: Choose verification method (radio buttons) -->
      <div v-if="verifyStep === 'method'">
        <p class="mb-2">
          <strong>ABHA:</strong> {{ verifyAbhaInput || verifyMobileInput || ('Aadhaar: ' + maskedVerifyAadhaar) }}
        </p>
        <p class="text-muted small mb-3">Choose a verification method:</p>
        <b-form-radio-group v-model="selectedAuthMethod" stacked class="mb-3">
          <b-form-radio v-if="verifyAuthMethods.includes('AADHAAR_OTP')" value="aadhaar">
            <i class="mdi mdi-shield-key mr-1"></i> Aadhaar OTP
          </b-form-radio>
          <b-form-radio v-if="verifyAuthMethods.includes('MOBILE_OTP')" value="abdm">
            <i class="mdi mdi-message-text mr-1"></i> Mobile OTP (ABDM)
          </b-form-radio>
          <b-form-radio v-if="verifyAuthMethods.includes('FACE')" value="face">
            <i class="mdi mdi-camera mr-1"></i> Face Authentication
          </b-form-radio>
        </b-form-radio-group>
        <div class="d-flex justify-content-end mt-2">
          <b-button variant="text" class="text-danger mr-2" @click="verifyDialog = false">Cancel</b-button>
          <b-button variant="primary" :disabled="!selectedAuthMethod || verifyLoading" @click="proceedWithAuthMethod()">
            <b-spinner small v-if="verifyLoading" class="mr-1"></b-spinner>
            Proceed
          </b-button>
        </div>
      </div>

      <!-- Step V3: Enter OTP -->
      <div v-if="verifyStep === 'otp'">
        <b-alert show variant="info" class="py-2 px-3 mb-2">
          <template v-if="verifyMaskedMobile">
            OTP sent to {{ verifyOtpSystem === 'aadhaar' ? 'Aadhaar registered' : 'ABHA linked' }}
            mobile number ending with <strong>{{ verifyMaskedMobile }}</strong>
          </template>
          <template v-else>
            OTP sent to your {{ verifyOtpSystem === 'aadhaar' ? 'Aadhaar-linked' : 'ABHA-linked' }} mobile number.
          </template>
        </b-alert>

        <form-wrapper @submit="loginVerifyOtp()">
          <input-field
            label="Enter 6-digit OTP"
            type="number"
            rules="required"
            v-model="verifyOtp"
          ></input-field>

          <!-- Resend OTP timer -->
          <div class="d-flex justify-content-end align-items-center mb-3">
            <div>
              <b-button
                variant="link"
                size="sm"
                :disabled="verifyResendTimer > 0 || verifyResendCount >= 2"
                @click="resendVerifyOtp()"
                class="p-0"
              >
                <template v-if="verifyResendTimer > 0">Resend in {{ verifyResendTimer }}s</template>
                <template v-else-if="verifyResendCount >= 2">Max resends reached</template>
                <template v-else>Resend OTP</template>
              </b-button>
            </div>
          </div>

          <div class="d-flex justify-content-end mt-3">
            <b-button variant="text" class="text-danger mr-2" @click="verifyDialog = false">Cancel</b-button>
            <b-button variant="primary" type="submit" :disabled="verifyLoading">
              <b-spinner small v-if="verifyLoading" class="mr-1"></b-spinner>
              Verify
            </b-button>
          </div>
        </form-wrapper>
      </div>

      <!-- Step V3F: Face Auth (QR-based) for verify -->
      <div v-if="verifyStep === 'face'">
        <div v-if="!faceAuthQrDataUrl && !faceAuthStatus">
          <p class="text-muted small mb-2">Initializing face authentication...</p>
          <div class="text-center"><b-spinner></b-spinner></div>
        </div>

        <div v-else-if="faceAuthStatus === 'COMPLETE'">
          <b-alert variant="success" show>
            <i class="mdi mdi-check-circle mr-1"></i>
            Face authentication completed! Verifying account...
          </b-alert>
          <div class="text-center"><b-spinner small></b-spinner></div>
        </div>

        <div v-else-if="faceAuthStatus === 'FAILED'">
          <b-alert variant="danger" show>
            <i class="mdi mdi-alert-circle mr-1"></i>
            Face authentication failed. Please try again.
          </b-alert>
          <b-button variant="primary" size="sm" @click="startFaceAuth('verify')">
            <i class="mdi mdi-refresh mr-1"></i> Retry
          </b-button>
        </div>

        <div v-else>
          <p class="text-muted small mb-2">
            Scan this QR code with the <strong>ABHA mobile app</strong> to complete face verification.
          </p>
          <div class="text-center mb-3">
            <img :src="faceAuthQrDataUrl" alt="Face Auth QR Code" class="border rounded" style="width: 250px; height: 250px;" />
          </div>
          <p class="text-center small">
            <span v-if="faceAuthStatus === 'PENDING'" class="text-warning">
              <b-spinner small class="mr-1"></b-spinner> Waiting for face capture...
            </span>
            <span v-else-if="faceAuthStatus === 'VERIFIED'" class="text-info">
              <b-spinner small class="mr-1"></b-spinner> Face verified, completing authentication...
            </span>
            <span v-else class="text-muted">
              <b-spinner small class="mr-1"></b-spinner> Waiting for QR scan...
            </span>
          </p>
        </div>

        <div class="d-flex justify-content-end mt-2">
          <b-button variant="text" class="text-danger" @click="cancelFaceAuth(); verifyStep = 'method'">Back</b-button>
        </div>
      </div>

      <!-- Step V4: Multiple accounts - select one -->
      <div v-if="verifyStep === 'accounts'">
        <p class="text-muted small mb-2">Multiple ABHA accounts found. Select one:</p>
        <b-list-group>
          <b-list-group-item
            v-for="(acc, idx) in multipleAccounts"
            :key="idx"
            button
            @click="selectAccount(acc)"
            class="d-flex justify-content-between align-items-center"
          >
            <div>
              <div class="d-flex align-items-center">
                <img v-if="acc.photo || acc.profilePhoto" :src="getProfilePhoto(acc)" width="40" height="40" class="rounded-circle mr-2" />
                <div>
                  <strong>{{ acc.ABHANumber || acc.abhaNumber }}</strong>
                  <br />
                  <small>{{ acc.name || [acc.firstName, acc.middleName, acc.lastName].filter(Boolean).join(' ') }}</small>
                  <br v-if="acc.preferredAbhaAddress || acc.preferredAddress" />
                  <small v-if="acc.preferredAbhaAddress || acc.preferredAddress" class="text-muted">{{ acc.preferredAbhaAddress || acc.preferredAddress }}</small>
                  <small v-if="acc.status" class="ml-1"><b-badge :variant="acc.status === 'ACTIVE' ? 'success' : 'secondary'" pill>{{ acc.status }}</b-badge></small>
                </div>
              </div>
            </div>
            <b-badge variant="primary" pill>Select</b-badge>
          </b-list-group-item>
        </b-list-group>
        <div class="d-flex justify-content-end mt-3">
          <b-button variant="text" class="text-danger" @click="verifyDialog = false">Cancel</b-button>
        </div>
      </div>

      <!-- Step V5: Verified - Profile + Card -->
      <div v-if="verifyStep === 'success'">
        <b-alert show variant="success" class="py-2 px-3">
          <strong>ABHA Verified Successfully!</strong>
        </b-alert>
        <div v-if="abhaProfile" class="mt-2">
          <table class="table table-sm table-bordered mb-2">
            <tr>
              <td class="font-weight-bold" width="40%">ABHA Number</td>
              <td>{{ abhaProfile.ABHANumber || abhaProfile.abhaNumber }}</td>
            </tr>
            <tr>
              <td class="font-weight-bold">Name</td>
              <td>{{ abhaProfile.firstName }} {{ abhaProfile.middleName }} {{ abhaProfile.lastName }}</td>
            </tr>
            <tr v-if="abhaProfile.preferredAddress">
              <td class="font-weight-bold">ABHA Address</td>
              <td>{{ abhaProfile.preferredAddress }}</td>
            </tr>
            <tr v-if="abhaProfile.mobile">
              <td class="font-weight-bold">Mobile</td>
              <td>{{ abhaProfile.mobile }}</td>
            </tr>
            <tr v-if="abhaProfile.gender">
              <td class="font-weight-bold">Gender</td>
              <td>{{ abhaProfile.gender }}</td>
            </tr>
            <tr v-if="abhaProfile.dob">
              <td class="font-weight-bold">Date of Birth</td>
              <td>{{ abhaProfile.dob }}</td>
            </tr>
            <tr v-if="abhaProfile.kycVerified !== undefined">
              <td class="font-weight-bold">KYC Verified</td>
              <td>
                <b-badge :variant="abhaProfile.kycVerified ? 'success' : 'warning'">
                  {{ abhaProfile.kycVerified ? "Yes" : "No" }}
                </b-badge>
              </td>
            </tr>
          </table>

          <p class="text-muted small">
            <i class="mdi mdi-lock mr-1"></i>
            Name, Date of Birth, and Gender are locked from ABHA and cannot be edited.
          </p>
        </div>

        <!-- ABHA Card -->
        <div class="mb-3">
          <b-button variant="outline-info" size="sm" class="mr-1" :disabled="cardLoading" @click="viewAbhaCard('verify')">
            <b-spinner small v-if="cardLoading" class="mr-1"></b-spinner>
            View ABHA Card
          </b-button>
          <b-button variant="outline-secondary" size="sm" :disabled="cardLoading" @click="downloadAbhaCard('verify')">
            Download ABHA Card (PDF)
          </b-button>
        </div>
        <div v-if="abhaCardBase64" class="text-center mb-3">
          <img :src="'data:image/png;base64,' + abhaCardBase64" class="border rounded" style="max-width: 100%;" />
        </div>

        <div class="d-flex justify-content-end mt-3">
          <b-button variant="primary" :disabled="linkLoading" @click="applyAbhaProfile()">
            <b-spinner small v-if="linkLoading" class="mr-1"></b-spinner>
            Link to Recipient
          </b-button>
        </div>
      </div>

      <!-- Step V-NOT-FOUND: No ABHA found -->
      <div v-if="verifyStep === 'not_found'">
        <b-alert show variant="warning" class="py-2 px-3">
          <strong>{{ notFoundMessage }}</strong>
        </b-alert>
        <div class="d-flex justify-content-end mt-3">
          <b-button variant="outline-primary" class="mr-2" @click="openCreateModal()">
            Create ABHA Instead
          </b-button>
          <b-button variant="text" class="text-danger" @click="verifyDialog = false">Close</b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import abdmService from "../../../assets/services/abdm.service";
import Help from "../../Help.vue";

const RD_SERVICE_URL = "http://localhost:11100/rd/capture";
const FINGERPRINT_PID_OPTIONS =
  '<PidOptions ver="1.0"><Opts fCount="1" fType="2" iCount="0" pCount="0" format="0" pidVer="2.0" timeout="20000" otp="" wadh="E0jzJ/P8UopUHAieZn8CKqS4WPMi5ZSYXgfnlfkWjrc=" env="P" posh="RIGHT_INDEX" /></PidOptions>';

export default {
  props: {
    aadhaarNumber: {
      type: String,
      default: "",
    },
    isAadharVerified: {
      type: Boolean,
      default: false,
    },
    beneficiaryName: {
      type: String,
      default: "",
    },
    phoneNumber: {
      type: String,
      default: "",
    },
  },
  components: {
    Help,
  },
  data() {
    return {
      // --- Create ABHA flow ---
      createDialog: false,
      createStep: "consent",
      createLoading: false,
      createMethod: "",
      createOtp: "",
      createMobile: "",
      createTxnId: "",
      createXToken: "",
      createMaskedMobile: "",

      // Consent (NHA M1 - GAP-01)
      consent: {
        aadhaar_sharing: false,
        legacy_records: false,
        health_records: false,
        anonymization: false,
        worker_confirm: false,
        beneficiary_confirm: false,
      },
      consentError: "",

      // Resend OTP (NHA M1 - GAP-02)
      resendCount: 0,
      resendTimer: 0,
      resendInterval: null,

      // Communication Mobile (NHA M1 - GAP-05)
      commMobile: "",
      commMobileOtp: "",
      commMobileOtpSent: false,
      aadhaarLinkedMobile: "",
      commResendCount: 0,
      commResendTimer: 0,
      commResendInterval: null,

      // ABHA Address (NHA M1 - GAP-06)
      addressSuggestions: [],
      selectedAddress: "",
      customAddress: "",
      addressError: "",
      createdAbhaAddress: "",
      isExistingAccount: false,

      // ABHA Card (NHA M1 - GAP-07)
      abhaCardBase64: "",
      cardLoading: false,

      // Uniqueness check (NHA M1 - GAP-13)
      linkLoading: false,

      // --- Verify ABHA flow ---
      verifyDialog: false,
      verifyStep: "search",
      verifyLoading: false,
      verifyMethod: "",
      verifyTabIndex: 0,
      verifyAbhaInput: "",
      verifyMobileInput: "",
      verifyAadhaarInput: "",
      verifyOtp: "",
      verifyTxnId: "",
      verifyXToken: "",
      verifyRefreshToken: "",
      verifyTToken: "",
      verifyOtpSystem: "abdm",
      verifyMaskedMobile: "",
      verifyResendCount: 0,
      verifyResendTimer: 0,
      verifyResendInterval: null,
      verifyOtpExpiry: 0,
      verifyOtpExpiryInterval: null,
      multipleAccounts: [],
      notFoundMessage: "",
      verifySearchType: "abha",
      // FIND ABHA flow (mobile search → index-based OTP)
      searchTxnId: "",
      searchAccountIndex: null,
      searchAbhaList: [],  // All ABHA accounts from search (for showing after aadhaar OTP verify)

      // Demo auth (Fix #9)
      demoName: "",
      demoGender: "",
      demoDob: "",
      demoMobile: "",
      demoStateCode: "",
      demoDistrictCode: "",
      demoAddress: "",
      demoPincode: "",

      // Dynamic auth methods (Fix #10 & #11)
      verifyAuthMethods: ["AADHAAR_OTP", "MOBILE_OTP"],
      selectedAuthMethod: "",

      // Face auth (QR-based)
      faceAuthTxnId: "",
      faceAuthQrUrl: "",
      faceAuthQrDataUrl: "",
      faceAuthStatus: "",
      faceAuthPollTimer: null,
      faceAuthPollCount: 0,

      // Shared
      abhaProfile: null,
    };
  },
  computed: {
    maskedAadhaar() {
      if (!this.aadhaarNumber || this.aadhaarNumber.length < 12) return "****-****-****";
      return "XXXX-XXXX-" + this.aadhaarNumber.substring(8);
    },
    maskedVerifyAadhaar() {
      if (!this.verifyAadhaarInput || this.verifyAadhaarInput.length < 12) return "****-****-****";
      return "XXXX-XXXX-" + this.verifyAadhaarInput.substring(8);
    },
    workerName() {
      // Get logged-in user name from Vuex store
      try {
        return this.$store.getters.userName || this.$store.state.userName || "";
      } catch (e) {
        return "";
      }
    },
    cmSuffix() {
      return "sbx"; // Change to "abdm" for production
    },
    resolvedAbhaAddress() {
      if (this.createdAbhaAddress) return this.createdAbhaAddress;
      if (this.abhaProfile) return this.abhaProfile.preferredAddress || this.abhaProfile.preferredAbhaAddress || "";
      return "";
    },
  },
  watch: {
    "consent.beneficiary_confirm"(val) {
      if (val) {
        this.consent.aadhaar_sharing = true;
        this.consent.legacy_records = true;
        this.consent.health_records = true;
        this.consent.anonymization = true;
        this.consent.worker_confirm = true;
      }
    },
  },
  methods: {
    // ==========================================
    // ABDM Error Message Helper
    // ==========================================
    getAbdmError(err, fallback) {
      // Extract ABDM error message from axios error response
      let msg = "";
      if (err && err.response && err.response.data && err.response.data.msg) {
        msg = err.response.data.msg;
        // Strip "ABDM API Error (StatusCode): " prefix
        let match = msg.match(/^ABDM API Error \([^)]+\):\s*(.+)$/);
        if (match) msg = match[1];
      } else {
        msg = err && err.message ? err.message : "";
      }
      if (!msg) return fallback || "Something went wrong. Please try again.";
      // Map technical ABDM/UIDAI errors to user-friendly messages
      let lower = msg.toLowerCase();
      if (lower.includes("otp validation failed") || lower.includes("otp did not match") || lower.includes("invalid otp") || lower.includes("mismatch"))
        return "Incorrect OTP entered. Please check and try again.";
      if (lower.includes("otp expired") || lower.includes("transaction not found"))
        return "OTP has expired. Please request a new OTP and try again.";
      if (lower.includes("invalid aadhaar") || lower.includes("invalid uidai"))
        return "Invalid Aadhaar number. Please check and try again.";
      if (lower.includes("already exist"))
        return msg; // "This account already exist" — show as-is
      if (lower.includes("not found") || lower.includes("no abha"))
        return "ABHA account not found. Please check the details and try again.";
      if (lower.includes("invalid benefit"))
        return "Demo Auth is not available. Please contact support.";
      if (lower.includes("invalid scope") || lower.includes("invalid login"))
        return "Authentication method not supported. Please try a different method.";
      // For other errors, strip UIDAI prefix for cleaner display
      msg = msg.replace(/^UIDAI Error code\s*:\s*\d+\s*:\s*/i, "");
      return msg;
    },

    // ==========================================
    // Consent Validation (GAP-01)
    // ==========================================
    validateConsent(flow) {
      this.consentError = "";
      if (!this.consent.aadhaar_sharing) {
        this.consentError = "You must consent to Aadhaar sharing to proceed.";
        return;
      }
      if (!this.consent.worker_confirm) {
        this.consentError = "Healthcare worker must confirm that consent has been explained to the beneficiary.";
        return;
      }
      if (!this.consent.beneficiary_confirm) {
        this.consentError = "Beneficiary must confirm that they have been explained about the consent.";
        return;
      }

      if (flow === "create") {
        this.createStep = "method";
      } else {
        // After consent in verify flow, proceed to method selection
        this.verifyStep = "method";
      }
    },

    // ==========================================
    // Resend OTP Timer (GAP-02)
    // ==========================================
    startResendTimer(flow) {
      if (flow === "create") {
        this.resendTimer = 60;
        clearInterval(this.resendInterval);
        this.resendInterval = setInterval(() => {
          this.resendTimer--;
          if (this.resendTimer <= 0) clearInterval(this.resendInterval);
        }, 1000);
      } else if (flow === "verify") {
        this.verifyResendTimer = 60;
        clearInterval(this.verifyResendInterval);
        this.verifyResendInterval = setInterval(() => {
          this.verifyResendTimer--;
          if (this.verifyResendTimer <= 0) clearInterval(this.verifyResendInterval);
        }, 1000);
        // OTP expiry countdown: Aadhaar OTP = 10 min, ABDM Mobile OTP = 10 min
        this.verifyOtpExpiry = 600;
        clearInterval(this.verifyOtpExpiryInterval);
        this.verifyOtpExpiryInterval = setInterval(() => {
          this.verifyOtpExpiry--;
          if (this.verifyOtpExpiry <= 0) clearInterval(this.verifyOtpExpiryInterval);
        }, 1000);
      } else if (flow === "comm") {
        this.commResendTimer = 60;
        clearInterval(this.commResendInterval);
        this.commResendInterval = setInterval(() => {
          this.commResendTimer--;
          if (this.commResendTimer <= 0) clearInterval(this.commResendInterval);
        }, 1000);
      }
    },
    // Parse masked mobile number from ABDM OTP message
    // e.g. "OTP sent to Aadhaar registered mobile number ending with ******0903"
    parseMobileFromMessage(msg) {
      if (!msg) return "";
      let match = msg.match(/ending\s+with\s*\**(\*+\d+)/i);
      if (match) return match[1];
      // fallback: extract last sequence of *s and digits
      let fallback = msg.match(/(\*{2,}\d{2,})/);
      return fallback ? fallback[1] : "";
    },
    formatOtpExpiry(seconds) {
      if (seconds <= 0) return "0:00";
      let m = Math.floor(seconds / 60);
      let s = seconds % 60;
      return m + ":" + (s < 10 ? "0" : "") + s;
    },

    async resendCreateOtp() {
      if (this.resendCount >= 2) return;
      this.resendCount++;
      await this.enrollmentRequestOtp(true);
    },

    async resendVerifyOtp() {
      if (this.verifyResendCount >= 2) return;
      this.verifyResendCount++;
      await this.loginRequestOtp(this.verifyOtpSystem, true);
    },

    async resendCommMobileOtp() {
      if (this.commResendCount >= 2) return;
      this.commResendCount++;
      await this.sendCommMobileOtp(true);
    },

    // ==========================================
    // ABHA Address Validation (GAP-06)
    // ==========================================
    validateAbhaAddress(addr) {
      if (!addr) return "ABHA address is required.";
      if (addr.length < 8) return "ABHA address must be at least 8 characters.";
      if (addr.length > 18) return "ABHA address must be at most 18 characters.";
      if (/^[^a-zA-Z0-9]/.test(addr)) return "ABHA address cannot start with a special character.";
      if (/[^a-zA-Z0-9]$/.test(addr)) return "ABHA address cannot end with a special character.";
      if ((addr.match(/\./g) || []).length > 1) return "Only one dot (.) is allowed.";
      if ((addr.match(/_/g) || []).length > 1) return "Only one underscore (_) is allowed.";
      if (!/^[a-zA-Z0-9._]+$/.test(addr)) return "Only letters, numbers, dot (.) and underscore (_) are allowed.";
      return null;
    },

    // ==========================================
    // Create ABHA Flow
    // ==========================================
    openCreateModal() {
      if (!this.aadhaarNumber || this.aadhaarNumber.length !== 12) {
        this.$alert.show("Aadhaar Number is not valid");
        return;
      }
      // Reset all create state
      this.createStep = "consent";
      this.createMethod = "";
      this.createOtp = "";
      this.createMobile = this.phoneNumber || "";
      this.createTxnId = "";
      this.createXToken = "";
      this.createMaskedMobile = "";
      this.abhaProfile = null;
      this.abhaCardBase64 = "";
      this.cancelFaceAuth();
      this.faceAuthTxnId = "";
      this.faceAuthQrUrl = "";
      this.faceAuthQrDataUrl = "";
      this.faceAuthStatus = "";
      this.faceAuthPollCount = 0;
      this.resendCount = 0;
      this.resendTimer = 0;
      this.commMobile = "";
      this.commMobileOtp = "";
      this.commMobileOtpSent = false;
      this.commResendCount = 0;
      this.commResendTimer = 0;
      this.addressSuggestions = [];
      this.selectedAddress = "";
      this.customAddress = "";
      this.addressError = "";
      this.createdAbhaAddress = "";
      this.isExistingAccount = false;
      this.demoName = "";
      this.demoGender = "";
      this.demoDob = "";
      this.demoMobile = "";
      this.demoStateCode = "";
      this.demoDistrictCode = "";
      this.demoAddress = "";
      this.demoPincode = "";
      this.consentError = "";
      // Reset consent to defaults
      this.consent = {
        aadhaar_sharing: false,
        legacy_records: false,
        health_records: false,
        anonymization: false,
        worker_confirm: false,
        beneficiary_confirm: false,
      };
      this.verifyDialog = false;
      this.createDialog = true;
    },

    // -- OTP Enrollment --
    async enrollmentRequestOtp(isResend) {
      this.createMethod = "otp";
      this.createLoading = true;
      try {
        let { data } = await abdmService.enrollmentRequestOtp({
          aadhaarNumber: this.aadhaarNumber,
        });
        if (!data.status) {
          this.$alert.show(data.msg || "Failed to send OTP.");
          return;
        }
        this.createTxnId = data.txnId;
        // Try mobileNumber first, then parse from ABDM message
        let maskedMob = data.mobileNumber || this.parseMobileFromMessage(data.msg) || "";
        this.createMaskedMobile = maskedMob;
        this.aadhaarLinkedMobile = maskedMob;
        if (!isResend) {
          this.createStep = "otp";
        }
        this.startResendTimer("create");
      } catch (err) {
        this.$alert.show(this.getAbdmError(err, "Failed to send OTP. Please try again."));
      } finally {
        this.createLoading = false;
      }
    },

    async enrollmentVerifyOtp() {
      this.createLoading = true;
      try {
        let { data } = await abdmService.enrollByAadhaar({
          transactionId: this.createTxnId,
          otp: this.createOtp,
          mobile: this.createMobile || "",
          // NHA M1 Consent data (GAP-01)
          consent_aadhaar_sharing: this.consent.aadhaar_sharing,
          consent_legacy_records: this.consent.legacy_records,
          consent_health_records: this.consent.health_records,
          consent_anonymization: this.consent.anonymization,
          consent_worker_confirm: this.consent.worker_confirm,
          consent_beneficiary_confirm: this.consent.beneficiary_confirm,
          consent_worker_name: this.workerName,
          consent_beneficiary_name: this.beneficiaryName,
          consent_timestamp: new Date().toISOString(),
        });
        if (!data.status) {
          this.$alert.show(data.msg || "OTP verification failed.");
          return;
        }
        this.abhaProfile = data.profile || data;
        this.createXToken = data.xToken || "";
        this.createTxnId = data.txnId || this.createTxnId;

        // Check if account already exists (will skip address selection later)
        if (data.isNew === false) {
          this.isExistingAccount = true;
        }

        // Check if communication mobile differs from Aadhaar-linked mobile (GAP-05)
        // API returns masked number (e.g. "XXXXXX1234"), so compare last 4 digits only
        let profileMobile = (
          this.abhaProfile.mobile ||
          this.abhaProfile.mobileNumber ||
          this.aadhaarLinkedMobile ||
          this.createMaskedMobile ||
          ""
        ).replace(/\D/g, "");
        let commMobile = this.createMobile.trim();
        let profileLast4 = profileMobile.slice(-4);
        let commLast4 = commMobile.slice(-4);
        if (commMobile && profileLast4 && commLast4 !== profileLast4) {
          // Last 4 digits do NOT match — user entered a different mobile, verify it
          this.commMobile = commMobile;
          this.commMobileOtp = "";
          this.commMobileOtpSent = false;
          this.commResendCount = 0;
          this.commResendTimer = 0;
          this.createStep = "mobile_verify";
        } else if (commMobile && !profileLast4) {
          // No Aadhaar mobile available to compare — still verify the comm mobile
          this.commMobile = commMobile;
          this.commMobileOtp = "";
          this.commMobileOtpSent = false;
          this.commResendCount = 0;
          this.commResendTimer = 0;
          this.createStep = "mobile_verify";
        } else if (this.isExistingAccount) {
          // Existing account + mobile matches — skip to success
          this.createStep = "success";
        } else {
          // Last 4 digits match — skip mobile verify, go to address selection
          this.loadAddressSuggestions();
        }
      } catch (err) {
        this.$alert.show(this.getAbdmError(err, "Enrollment failed. Please try again."));
      } finally {
        this.createLoading = false;
      }
    },

    // -- Communication Mobile Verification (GAP-05) --
    async sendCommMobileOtp(isResend) {
      this.createLoading = true;
      try {
        let { data } = await abdmService.enrollmentMobileRequestOtp({
          transactionId: this.createTxnId,
          mobileNumber: this.commMobile,
        });
        if (!data.status) {
          this.$alert.show(data.msg || "Failed to send mobile OTP.");
          return;
        }
        this.createTxnId = data.txnId || this.createTxnId;
        this.commMobileOtpSent = true;
        if (!isResend) {
          this.startResendTimer("comm");
        } else {
          this.startResendTimer("comm");
        }
      } catch (err) {
        this.$alert.show(this.getAbdmError(err, "Failed to send mobile OTP."));
      } finally {
        this.createLoading = false;
      }
    },

    async verifyCommMobileOtp() {
      this.createLoading = true;
      try {
        let { data } = await abdmService.enrollmentMobileVerify({
          transactionId: this.createTxnId,
          otp: this.commMobileOtp,
        });
        if (!data.status) {
          this.$alert.show(data.msg || "Mobile OTP verification failed.");
          return;
        }
        this.createXToken = data.xToken || this.createXToken;
        this.createTxnId = data.txnId || this.createTxnId;
        if (this.isExistingAccount) {
          this.createStep = "success";
        } else {
          this.loadAddressSuggestions();
        }
      } catch (err) {
        this.$alert.show(this.getAbdmError(err, "Mobile verification failed."));
      } finally {
        this.createLoading = false;
      }
    },

    // -- ABHA Address Suggestions (GAP-06) --
    async loadAddressSuggestions() {
      this.createLoading = true;
      try {
        let { data } = await abdmService.enrollmentGetSuggestions({
          xToken: this.createXToken,
          transactionId: this.createTxnId,
        });
        if (data.status && data.abhaAddressList && data.abhaAddressList.length > 0) {
          this.addressSuggestions = data.abhaAddressList;
          this.selectedAddress = data.abhaAddressList[0];
        } else {
          this.addressSuggestions = [];
          this.selectedAddress = "__custom__";
        }
        this.createStep = "address";
      } catch (err) {
        // If suggestion fetch fails, still show address step with custom input
        this.addressSuggestions = [];
        this.selectedAddress = "__custom__";
        this.createStep = "address";
      } finally {
        this.createLoading = false;
      }
    },

    async submitAbhaAddress() {
      this.addressError = "";
      let address = this.selectedAddress === "__custom__" ? this.customAddress.trim() : this.selectedAddress;

      if (this.selectedAddress === "__custom__" || this.addressSuggestions.length === 0) {
        let err = this.validateAbhaAddress(address);
        if (err) {
          this.addressError = err;
          return;
        }
      }

      this.createLoading = true;
      try {
        let { data } = await abdmService.enrollmentCreateAddress({
          transactionId: this.createTxnId,
          preferredAbhaAddress: address,
          xToken: this.createXToken,
        });
        if (!data.status) {
          if ((data.msg || "").toLowerCase().includes("already")) {
            this.addressError = "ABHA Address already exists. Please choose a different one.";
          } else {
            this.$alert.show(data.msg || "Failed to create ABHA address.");
          }
          return;
        }
        this.createdAbhaAddress = data.abhaAddress || address;
        if (this.abhaProfile) {
          this.abhaProfile.preferredAddress = this.createdAbhaAddress;
        }
        this.createStep = "success";
      } catch (err) {
        this.$alert.show(this.getAbdmError(err, "Failed to create ABHA address."));
      } finally {
        this.createLoading = false;
      }
    },

    // -- Fingerprint Enrollment --
    enrollByFingerprint() {
      const vm = this;
      vm.createMethod = "fingerprint";
      vm.createLoading = true;
      vm.captureFingerprint(function (err, bioData) {
        if (err) {
          vm.createLoading = false;
          return;
        }
        vm.submitFingerprintEnrollment(bioData);
      });
    },

    async submitFingerprintEnrollment(bioData) {
      try {
        let { data } = await abdmService.enrollByBiometric({
          aadhaarNumber: this.aadhaarNumber,
          bioType: "FIR",
          bioData: bioData,
          mobile: "",
        });
        if (!data.status) {
          this.$alert.show(data.msg || "Fingerprint verification failed.");
          return;
        }
        this.abhaProfile = data.profile || data;
        this.createXToken = data.xToken || "";
        this.createTxnId = data.txnId || this.createTxnId;
        if (data.isNew === false) {
          this.isExistingAccount = true;
          this.createStep = "success";
          return;
        }
        this.loadAddressSuggestions();
      } catch (err) {
        this.$alert.show(this.getAbdmError(err, "Fingerprint enrollment failed. Please try again."));
      } finally {
        this.createLoading = false;
      }
    },

    // ==========================================
    // Face Auth (QR-based)
    // ==========================================

    async startFaceAuth(flow) {
      this.faceAuthStatus = "";
      this.faceAuthQrDataUrl = "";
      this.faceAuthTxnId = "";
      this.faceAuthPollCount = 0;

      if (flow === "create") {
        this.createStep = "face";
        this.createLoading = true;
      } else {
        this.verifyStep = "face";
        this.verifyLoading = true;
      }

      try {
        let { data } = await abdmService.faceAuthInit();
        if (!data.status || data.status === "0") {
          this.$alert.show(data.msg || "Failed to initialize face authentication.");
          if (flow === "create") this.createStep = "method";
          else this.verifyStep = "method";
          return;
        }

        this.faceAuthTxnId = data.txnId;

        const QRCode = (await import("qrcode")).default;
        this.faceAuthQrDataUrl = await QRCode.toDataURL(data.qrUrl, {
          width: 250,
          margin: 2,
        });

        this.faceAuthStatus = "PENDING";
        this.startFaceAuthPolling(flow);
      } catch (err) {
        this.$alert.show(this.getAbdmError(err, "Failed to initialize face authentication."));
        if (flow === "create") this.createStep = "method";
        else this.verifyStep = "method";
      } finally {
        if (flow === "create") this.createLoading = false;
        else this.verifyLoading = false;
      }
    },

    startFaceAuthPolling(flow) {
      this.cancelFaceAuth();
      this.faceAuthPollTimer = setInterval(async () => {
        this.faceAuthPollCount++;

        if (this.faceAuthPollCount > 60) {
          this.cancelFaceAuth();
          this.faceAuthStatus = "FAILED";
          this.$alert.show("Face authentication timed out. Please try again.");
          return;
        }

        try {
          let { data } = await abdmService.faceCapturePID({ txnId: this.faceAuthTxnId });
          this.faceAuthStatus = data.status;

          if (data.status === "COMPLETE") {
            this.cancelFaceAuth();
            if (data.txnId) this.faceAuthTxnId = data.txnId;
            if (flow === "create") {
              await this.completeFaceAuthEnroll();
            } else {
              await this.completeFaceAuthLogin();
            }
          } else if (data.status === "FAILED") {
            this.cancelFaceAuth();
          }
        } catch (err) {
          console.warn("Face auth poll error:", err);
        }
      }, 5000);
    },

    async completeFaceAuthEnroll() {
      this.createLoading = true;
      try {
        let { data } = await abdmService.faceAuthEnroll({
          aadhaarNumber: this.aadhaarNumber,
          txnId: this.faceAuthTxnId,
          mobile: this.createMobile || "",
        });
        if (!data.status || data.status === "0") {
          this.$alert.show(data.msg || "Face auth enrollment failed.");
          this.faceAuthStatus = "FAILED";
          return;
        }
        this.abhaProfile = data.profile || data;
        this.createXToken = data.xToken || "";
        this.createTxnId = data.txnId || this.faceAuthTxnId;
        if (data.isNew === false) {
          this.isExistingAccount = true;
          this.createStep = "success";
          return;
        }
        this.loadAddressSuggestions();
      } catch (err) {
        this.$alert.show(this.getAbdmError(err, "Face auth enrollment failed."));
        this.faceAuthStatus = "FAILED";
      } finally {
        this.createLoading = false;
      }
    },

    async completeFaceAuthLogin() {
      this.verifyLoading = true;
      try {
        let abhaNumber = "";
        if (this.verifySearchType === "abha") {
          abhaNumber = this.verifyAbhaInput.trim();
        }
        let { data } = await abdmService.loginVerifyBiometric({
          abhaNumber: abhaNumber,
          bioType: "FACE",
          bioData: this.faceAuthTxnId,
        });
        if (!data.status) {
          this.$alert.show(data.msg || "Face verification failed.");
          this.faceAuthStatus = "FAILED";
          return;
        }
        this.handleLoginResponse(data);
      } catch (err) {
        this.$alert.show(this.getAbdmError(err, "Face login failed."));
        this.faceAuthStatus = "FAILED";
      } finally {
        this.verifyLoading = false;
      }
    },

    cancelFaceAuth() {
      if (this.faceAuthPollTimer) {
        clearInterval(this.faceAuthPollTimer);
        this.faceAuthPollTimer = null;
      }
    },

    // -- Demo Auth Enrollment (Fix #9) --
    formatDobForAbdm(dateStr) {
      // Convert YYYY-MM-DD (HTML date input) to DD-MM-YYYY (ABDM format)
      if (!dateStr || !dateStr.includes("-")) return dateStr;
      let parts = dateStr.split("-");
      if (parts.length === 3 && parts[0].length === 4) {
        return parts[2] + "-" + parts[1] + "-" + parts[0];
      }
      return dateStr;
    },
    async submitDemoAuth() {
      this.createLoading = true;
      try {
        let { data } = await abdmService.enrollByDemoAuth({
          aadhaarNumber: this.aadhaarNumber,
          name: this.demoName,
          gender: this.demoGender,
          dateOfBirth: this.formatDobForAbdm(this.demoDob),
          mobile: this.demoMobile || this.createMobile,
          stateCode: this.demoStateCode,
          districtCode: this.demoDistrictCode,
          address: this.demoAddress,
          pincode: this.demoPincode,
          benefitName: "HFWS_Karnataka",
        });
        if (!data.status || data.status == "0") {
          this.$alert.show(data.msg || "Demographic verification failed.");
          return;
        }
        // Demo auth returns profile (converted from flat ABDM format to standard ABHAProfile)
        // and xToken (account-level ABHA-N token with kycVerified, refreshToken, watermarked card access)
        this.abhaProfile = data.profile || null;
        this.createXToken = data.xToken || "";
        // Demo auth doesn't return a txnId in the response — extract from token if needed
        // The backend handles txnId extraction from JWT and DB storage.

        if (data.isNew === false) {
          // Existing ABHA account found via demo auth — go to success to show profile + card
          this.isExistingAccount = true;
          this.createStep = "success";
          return;
        }
        // New ABHA — proceed to address selection (same as Aadhaar OTP flow)
        this.loadAddressSuggestions();
      } catch (err) {
        this.$alert.show(this.getAbdmError(err, "Demographic verification failed."));
      } finally {
        this.createLoading = false;
      }
    },

    // -- Proceed with selected auth method (Fix #11) --
    // Map ABDM auth method names to otpSystem values
    normalizeOtpSystem(authMethod) {
      if (!authMethod) return "abdm";
      let lower = authMethod.toLowerCase();
      if (lower === "aadhaar_otp" || lower === "aadhaar") return "aadhaar";
      // MOBILE_OTP, DEMOGRAPHICS, PASSWORD etc. → "abdm"
      return "abdm";
    },
    proceedWithAuthMethod() {
      if (this.selectedAuthMethod === "face" || this.selectedAuthMethod === "FACE_OTP") {
        this.startFaceAuth("verify");
      } else {
        this.loginRequestOtp(this.normalizeOtpSystem(this.selectedAuthMethod));
      }
    },

    // ==========================================
    // Verify ABHA Flow
    // ==========================================
    openVerifyModal() {
      this.verifyStep = "search";
      this.verifyMethod = "";
      this.verifyTabIndex = 0;
      this.verifyAbhaInput = "";
      this.verifyMobileInput = "";
      this.verifyAadhaarInput = "";
      this.verifyOtp = "";
      this.verifyTxnId = "";
      this.verifyXToken = "";
      this.verifyRefreshToken = "";
      this.verifyTToken = "";
      this.verifyOtpSystem = "abdm";
      this.verifyMaskedMobile = "";
      this.verifyResendCount = 0;
      this.verifyResendTimer = 0;
      this.verifyOtpExpiry = 0;
      clearInterval(this.verifyOtpExpiryInterval);
      this.verifyOtpExpiryInterval = null;
      this.multipleAccounts = [];
      this.abhaProfile = null;
      this.abhaCardBase64 = "";
      this.cancelFaceAuth();
      this.faceAuthTxnId = "";
      this.faceAuthQrUrl = "";
      this.faceAuthQrDataUrl = "";
      this.faceAuthStatus = "";
      this.faceAuthPollCount = 0;
      this.notFoundMessage = "";
      this.verifySearchType = "abha";
      this.searchTxnId = "";
      this.searchAccountIndex = null;
      this.searchAbhaList = [];
      this.verifyAuthMethods = ["AADHAAR_OTP", "MOBILE_OTP"];
      this.selectedAuthMethod = "";
      this.consentError = "";
      this.consent = {
        aadhaar_sharing: true,
        legacy_records: true,
        health_records: true,
        anonymization: true,
        worker_confirm: false,
        beneficiary_confirm: false,
      };
      this.createDialog = false;
      this.verifyDialog = true;
    },

    // -- Tab 1: ABHA Number / Address Search --
    async onVerifySearchSubmit() {
      let input = this.verifyAbhaInput.trim();
      if (!input) {
        this.$alert.show("Please enter an ABHA Number or ABHA Address.");
        return;
      }
      let isAddress = input.includes("@");
      if (!isAddress && input.replace(/-/g, "").length !== 14) {
        this.$alert.show("Please enter a valid 14-digit ABHA Number (e.g., 91-1234-5678-9012) or ABHA Address (e.g., user@abdm).");
        return;
      }
      if (isAddress) {
        this.verifyLoading = true;
        try {
          let { data } = await abdmService.abhaAddressSearch({ abhaAddress: input });
          if (data.status == "1" && data.data) {
            // If search returns ABHA number, switch to standard ABHA number flow
            // This gives account tokens → correct kycVerified + ABHA card with watermark
            if (data.data.abhaNumber) {
              this.verifyAbhaInput = data.data.abhaNumber;
              this.verifySearchType = "abha";
              this.verifyAuthMethods = ["AADHAAR_OTP", "MOBILE_OTP"];
            } else {
              // No ABHA number returned — stay on ABHA address PHR flow
              this.verifySearchType = "abha_address";
              this.verifyAuthMethods = data.data.authMethods || ["MOBILE_OTP"];
            }
          }
        } catch (e) {
          // Fallback — use ABHA address PHR flow
          this.verifySearchType = "abha_address";
          this.verifyAuthMethods = ["MOBILE_OTP"];
        }
        this.verifyLoading = false;
        this.selectedAuthMethod = "";
        this.verifyStep = "method";
      } else {
        this.verifySearchType = "abha";
        this.verifyAuthMethods = ["AADHAAR_OTP", "MOBILE_OTP"];
        this.selectedAuthMethod = "";
        this.verifyStep = "method";
      }
    },

    // -- Tab 2: Mobile Number Search (GAP-09) --
    // Uses FIND ABHA flow: search → get txnId + index → loginHint "index" (supports both OTP types)
    async onVerifyMobileSearch() {
      let mobile = this.verifyMobileInput.trim();
      if (!mobile || mobile.length !== 10 || !/^\d{10}$/.test(mobile)) {
        this.$alert.show("Please enter a valid 10-digit mobile number.");
        return;
      }
      this.verifyLoading = true;
      this.verifySearchType = "mobile";
      this.searchTxnId = "";
      this.searchAccountIndex = null;
      try {
        let { data } = await abdmService.loginSearch({ mobileNumber: mobile });
        if (data.status == "1" && data.data) {
          // ABDM search returns array: [{ txnId, ABHA: [{ index, ABHANumber, name, gender }] }]
          let searchData = Array.isArray(data.data) ? data.data : [data.data];
          if (searchData.length > 0 && searchData[0].txnId) {
            this.searchTxnId = searchData[0].txnId;
            // Save full ABHA list for showing after aadhaar OTP verify
            this.searchAbhaList = searchData[0].ABHA || [];
            // Use first account index for FIND ABHA flow
            if (this.searchAbhaList.length > 0) {
              this.searchAccountIndex = this.searchAbhaList[0].index || 1;
            } else {
              this.searchAccountIndex = 1;
            }
          }
          // Mobile tab → directly send Mobile OTP (no radio selection needed)
          this.loginRequestOtp("abdm");
        } else {
          this.notFoundMessage = "ABHA Number not found. We did not find any ABHA number linked to this mobile number. Please use ABHA linked mobile number.";
          this.verifyStep = "not_found";
        }
      } catch (err) {
        let msg = this.getAbdmError(err, "");
        if (msg.toLowerCase().includes("not found") || msg.includes("404")) {
          this.notFoundMessage = "ABHA Number not found. We did not find any ABHA number linked to this mobile number. Please use ABHA linked mobile number.";
          this.verifyStep = "not_found";
        } else {
          // Fallback: try direct mobile OTP
          this.loginRequestOtp("abdm");
        }
      } finally {
        this.verifyLoading = false;
      }
    },

    // -- Tab 3: Aadhaar Number Search (GAP-10) --
    onVerifyAadhaarSearch() {
      let aadhaar = this.verifyAadhaarInput.trim();
      if (!aadhaar || aadhaar.length !== 12 || !/^\d{12}$/.test(aadhaar)) {
        this.$alert.show("Aadhaar Number is not valid");
        return;
      }
      this.verifySearchType = "aadhaar";
      // Aadhaar tab → directly send Aadhaar OTP (no radio selection needed)
      this.loginRequestOtp("aadhaar");
    },

    // -- OTP Login (GAP-08: supports aadhaar and abdm) --
    async loginRequestOtp(otpSystem, isResend) {
      this.verifyOtpSystem = otpSystem;
      this.verifyMethod = otpSystem === "aadhaar" ? "aadhaar_otp" : "mobile_otp";
      this.verifyLoading = true;
      try {
        let payload = { otpSystem: otpSystem };

        // Build payload based on search type
        if (this.verifySearchType === "abha_address" || this.verifySearchType === "abha_address_phr") {
          // ABHA address must use PHR endpoints (regular login doesn't support abha-address loginHint)
          try {
            let otpResult = await abdmService.abhaAddressRequestOtp({
              abhaAddress: this.verifyAbhaInput.trim(),
              authMethod: otpSystem === "aadhaar" ? "AADHAAR_OTP" : "MOBILE_OTP",
            });
            if (otpResult.data.status == "1") {
              this.verifyTxnId = otpResult.data.txnId;
              this.verifyMaskedMobile = otpResult.data.mobileNumber || "";
              this.verifySearchType = "abha_address_phr";
              if (!isResend) this.verifyStep = "otp";
              this.startResendTimer("verify");
              return;
            }
          } catch (phrErr) {
            this.$alert.show(this.getAbdmError(phrErr, "Failed to send OTP to ABHA address."));
            return;
          }
        } else if (this.verifySearchType === "abha") {
          payload.abhaNumber = this.verifyAbhaInput.trim();
        } else if (this.verifySearchType === "mobile") {
          if (otpSystem === "aadhaar" && this.searchTxnId && this.searchAccountIndex) {
            // Aadhaar OTP: must use FIND ABHA flow (loginHint "mobile" + aadhaar-verify not supported by ABDM)
            payload.searchTxnId = this.searchTxnId;
            payload.accountIndex = this.searchAccountIndex;
          } else {
            // Mobile OTP: direct loginHint "mobile" — returns all linked accounts after verify
            payload.mobileNumber = this.verifyMobileInput.trim();
          }
        } else if (this.verifySearchType === "aadhaar") {
          // Aadhaar loginHint — otpSystem from user choice (aadhaar or abdm)
          payload.aadhaarNumber = this.verifyAadhaarInput.trim();
        }

        let { data } = await abdmService.loginRequestOtp(payload);
        if (!data.status) {
          if ((data.msg || "").toLowerCase().includes("not found") || (data.msg || "").toLowerCase().includes("invalid")) {
            if (this.verifySearchType === "aadhaar") {
              this.notFoundMessage = "NO ABHA user registered with this Aadhaar Number.";
            } else {
              this.notFoundMessage = data.msg || "ABHA not found.";
            }
            this.verifyStep = "not_found";
            return;
          }
          this.$alert.show(data.msg || "Failed to send OTP.");
          return;
        }
        this.verifyTxnId = data.txnId;
        // ABDM returns masked mobile in message, not in a separate field
        this.verifyMaskedMobile = data.mobileNumber || this.parseMobileFromMessage(data.msg) || "";
        this.startResendTimer("verify");
        if (!isResend) {
          this.verifyStep = "otp";
        }
      } catch (err) {
        let msg = (err.response && err.response.data && err.response.data.msg) || err.message || "";
        if (msg.toLowerCase().includes("not found") || msg.toLowerCase().includes("no abha")) {
          if (this.verifySearchType === "aadhaar") {
            this.notFoundMessage = "NO ABHA user registered with this Aadhaar Number.";
          } else {
            this.notFoundMessage = "ABHA Number not found. We did not find any ABHA number linked to this identifier.";
          }
          this.verifyStep = "not_found";
        } else {
          this.$alert.show(msg || "Failed to send OTP.");
        }
      } finally {
        this.verifyLoading = false;
      }
    },

    async loginVerifyOtp() {
      this.verifyLoading = true;
      try {
        // ABHA address uses PHR verify endpoint
        if (this.verifySearchType === "abha_address_phr") {
          let result = await abdmService.abhaAddressVerify({
            transactionId: this.verifyTxnId,
            otp: this.verifyOtp,
          });
          let data = result.data;
          if (data.status != "1") {
            this.$alert.show(data.msg || "OTP verification failed. Please try again.");
            return;
          }
          this.verifyXToken = data.xToken || data.token || "";
          if (!this.verifyXToken) {
            this.$alert.show(data.msg || "OTP verification failed. No token received.");
            return;
          }
          this.verifyRefreshToken = data.refreshToken || "";
          // Fetch profile — backend tries regular account profile first (correct kycVerified),
          // falls back to PHR profile if PHR token can't access account endpoint
          try {
            let profileResult = await abdmService.abhaAddressProfile({
              xToken: this.verifyXToken,
              transactionId: this.verifyTxnId,
            });
            if (profileResult.data.status == "1" && profileResult.data.data) {
              this.abhaProfile = profileResult.data.data;
              // If backend succeeded with regular account profile, switch to standard flow
              // so ABHA card also uses the account endpoint (has watermark)
              if (profileResult.data.tokenType === "account") {
                this.verifySearchType = "abha";
              }
            }
          } catch (e) {
            // Profile fetch failed, continue without it
          }
          this.verifyStep = "success";
          return;
        }

        // Standard login verify (ABHA number, mobile, aadhaar)
        let { data } = await abdmService.loginVerify({
          transactionId: this.verifyTxnId,
          otp: this.verifyOtp,
          otpSystem: this.verifyOtpSystem,
        });
        if (data.status != "1") {
          // Show ABDM's exact error message
          this.$alert.show(data.msg || "OTP verification failed. Please try again.");
          return;
        }
        this.handleLoginResponse(data);
      } catch (err) {
        this.$alert.show(this.getAbdmError(err, "Verification failed. Please try again."));
      } finally {
        this.verifyLoading = false;
      }
    },

    // -- Fingerprint Login --
    verifyByFingerprint() {
      const vm = this;
      vm.verifyMethod = "fingerprint";
      vm.verifyLoading = true;
      vm.captureFingerprint(function (err, bioData) {
        if (err) {
          vm.verifyLoading = false;
          return;
        }
        vm.submitFingerprintLogin(bioData);
      });
    },

    async submitFingerprintLogin(bioData) {
      try {
        let abhaNumber = "";
        if (this.verifySearchType === "abha") {
          abhaNumber = this.verifyAbhaInput.trim();
        }
        let { data } = await abdmService.loginVerifyBiometric({
          abhaNumber: abhaNumber,
          bioType: "FIR",
          bioData: bioData,
        });
        if (!data.status) {
          this.$alert.show(data.msg || "Fingerprint verification failed.");
          return;
        }
        this.handleLoginResponse(data);
      } catch (err) {
        this.$alert.show(this.getAbdmError(err, "Fingerprint login failed. Please try again."));
      } finally {
        this.verifyLoading = false;
      }
    },

    // submitFaceLogin removed - replaced by completeFaceAuthLogin (QR-based)

    // -- Login response handler (shared between OTP/fingerprint/face) --
    async handleLoginResponse(resp) {
      if (resp.isMultipleAccounts && resp.accounts && resp.accounts.length > 1) {
        this.multipleAccounts = resp.accounts;
        this.verifyTToken = resp.token || "";
        this.verifyStep = "accounts";
        return;
      }

      // Single account — fetch full profile
      let token = resp.token || resp.xToken || "";
      this.verifyXToken = token;
      this.verifyRefreshToken = resp.refreshToken || "";

      if (!token) {
        this.$alert.show("OTP verification failed. Please check the OTP and try again.");
        return;
      }

      let profileFetched = false;
      try {
        // Pass refreshToken so backend can auto-refresh X-token if expired
        let profileResult = await abdmService.getProfile({
          xToken: token,
          transactionId: this.verifyTxnId,
          refreshToken: this.verifyRefreshToken,
        });
        if (profileResult.data.status == "1" && profileResult.data.data) {
          this.abhaProfile = profileResult.data.data;
          profileFetched = true;
          // Update tokens if backend refreshed them
          if (profileResult.data.xToken) this.verifyXToken = profileResult.data.xToken;
          if (profileResult.data.refreshToken) this.verifyRefreshToken = profileResult.data.refreshToken;
        }
      } catch (e) {
        // Profile fetch failed
      }
      if (!profileFetched) {
        this.abhaProfile = resp.ABHAProfile || resp;
      }

      this.verifyStep = "success";
    },

    async selectAccount(account) {
      // Standard flow — call login_verify_user with tToken (same for both MOBILE_OTP and AADHAAR_OTP)
      this.verifyLoading = true;
      try {
        let abhaNum = account.ABHANumber || account.abhaNumber;
        let { data } = await abdmService.loginVerifyUser({
          transactionId: this.verifyTxnId,
          abhaNumber: abhaNum,
          tToken: this.verifyTToken,
        });
        if (!data.status) {
          this.$alert.show(data.msg || "Account selection failed.");
          return;
        }
        this.verifyXToken = data.xToken || "";
        this.verifyRefreshToken = data.refreshToken || this.verifyRefreshToken;

        // Fetch full profile (pass refreshToken so backend can auto-refresh if X-token expired)
        if (this.verifyXToken) {
          let profileFetched = false;
          try {
            let profileResult = await abdmService.getProfile({
              xToken: this.verifyXToken,
              transactionId: this.verifyTxnId,
              refreshToken: this.verifyRefreshToken,
            });
            if (profileResult.data.status == "1" && profileResult.data.data) {
              this.abhaProfile = profileResult.data.data;
              profileFetched = true;
              if (profileResult.data.xToken) this.verifyXToken = profileResult.data.xToken;
              if (profileResult.data.refreshToken) this.verifyRefreshToken = profileResult.data.refreshToken;
            }
          } catch (e) {
            // Profile fetch failed
          }
          if (!profileFetched) {
            this.abhaProfile = data;
          }
        } else {
          this.abhaProfile = data;
        }

        this.verifyStep = "success";
      } catch (err) {
        this.$alert.show(this.getAbdmError(err, "Account selection failed."));
      } finally {
        this.verifyLoading = false;
      }
    },

    // ==========================================
    // ABHA Card View / Download (GAP-07)
    // ==========================================
    async viewAbhaCard(flow) {
      this.cardLoading = true;
      this.abhaCardBase64 = "";
      try {
        let xToken = flow === "create" ? this.createXToken : this.verifyXToken;
        let txnId = flow === "create" ? this.createTxnId : this.verifyTxnId;
        let refreshToken = flow === "create" ? "" : this.verifyRefreshToken;
        let data;
        // Always try regular account card first (has watermark).
        // If it fails (e.g. PHR token can't access account endpoint),
        // fall back to PHR card endpoint (works but without watermark).
        try {
          let result = await abdmService.getAbhaCard({ xToken, transactionId: txnId, refreshToken, format: "PNG" });
          data = result.data;
        } catch (e) {
          // Fallback to PHR card for ABHA address tokens
          try {
            let result = await abdmService.abhaAddressPhrCard({ xToken, transactionId: txnId, refreshToken });
            data = result.data;
          } catch (e2) {
            throw e; // throw original error if both fail
          }
        }
        if (data.status && data.cardBase64) {
          this.abhaCardBase64 = data.cardBase64;
          if (data.xToken) { if (flow === "create") this.createXToken = data.xToken; else this.verifyXToken = data.xToken; }
          if (data.refreshToken) { if (flow === "verify") this.verifyRefreshToken = data.refreshToken; }
        } else {
          this.$alert.show("Failed to retrieve ABHA card.");
        }
      } catch (err) {
        this.$alert.show(this.getAbdmError(err, "Failed to retrieve ABHA card."));
      } finally {
        this.cardLoading = false;
      }
    },

    async downloadAbhaCard(flow) {
      this.cardLoading = true;
      try {
        let xToken = flow === "create" ? this.createXToken : this.verifyXToken;
        let txnId = flow === "create" ? this.createTxnId : this.verifyTxnId;
        let refreshToken = flow === "create" ? "" : this.verifyRefreshToken;
        let data;
        // Always try regular account card first (has watermark).
        // If it fails (e.g. PHR token can't access account endpoint),
        // fall back to PHR card endpoint (works but without watermark).
        try {
          let result = await abdmService.getAbhaCard({ xToken, transactionId: txnId, refreshToken, format: "PDF" });
          data = result.data;
        } catch (e) {
          // Fallback to PHR card for ABHA address tokens
          try {
            let result = await abdmService.abhaAddressPhrCard({ xToken, transactionId: txnId, refreshToken });
            data = result.data;
          } catch (e2) {
            throw e; // throw original error if both fail
          }
        }
        if (data.status && data.cardBase64) {
          // Update tokens if refreshed
          if (data.xToken) { if (flow === "create") this.createXToken = data.xToken; else this.verifyXToken = data.xToken; }
          if (data.refreshToken) { if (flow === "verify") this.verifyRefreshToken = data.refreshToken; }
          let actualType = data.contentType || "image/png";
          // Always download as PDF — convert image to PDF if ABDM returned image
          if (actualType.includes("pdf")) {
            // Already PDF from ABDM
            let byteChars = atob(data.cardBase64);
            let byteNumbers = new Array(byteChars.length);
            for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
            let blob = new Blob([new Uint8Array(byteNumbers)], { type: "application/pdf" });
            let url = URL.createObjectURL(blob);
            let link = document.createElement("a");
            link.href = url;
            link.download = "ABHA_Card.pdf";
            link.click();
            URL.revokeObjectURL(url);
          } else {
            // Image (PNG/SVG) — convert to PDF using canvas
            let img = new Image();
            img.src = "data:" + actualType + ";base64," + data.cardBase64;
            img.onload = () => {
              let w = img.width;
              let h = img.height;
              let canvas = document.createElement("canvas");
              canvas.width = w;
              canvas.height = h;
              let ctx = canvas.getContext("2d");
              ctx.fillStyle = "#FFFFFF";
              ctx.fillRect(0, 0, w, h);
              ctx.drawImage(img, 0, 0);
              let imgData = canvas.toDataURL("image/jpeg", 0.95);
              // Build minimal PDF with embedded JPEG
              let pdfContent = this.buildPdfFromImage(imgData, w, h);
              let blob = new Blob([pdfContent], { type: "application/pdf" });
              let url = URL.createObjectURL(blob);
              let link = document.createElement("a");
              link.href = url;
              link.download = "ABHA_Card.pdf";
              link.click();
              URL.revokeObjectURL(url);
            };
          }
        } else {
          this.$alert.show("Failed to download ABHA card.");
        }
      } catch (err) {
        this.$alert.show(this.getAbdmError(err, "Failed to download ABHA card."));
      } finally {
        this.cardLoading = false;
      }
    },

    // Build a minimal single-page PDF from a JPEG data URL
    buildPdfFromImage(jpegDataUrl, imgW, imgH) {
      let jpegBase64 = jpegDataUrl.split(",")[1];
      let jpegBytes = atob(jpegBase64);
      let jpegLen = jpegBytes.length;
      // PDF page size: fit image (72 DPI reference, scale to ~595x842 A4 max)
      let scale = Math.min(595 / imgW, 842 / imgH, 1);
      let pw = Math.round(imgW * scale);
      let ph = Math.round(imgH * scale);
      // Build PDF structure
      let objs = [];
      objs[1] = "1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n";
      objs[2] = "2 0 obj\n<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj\n";
      objs[3] = "3 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 " + pw + " " + ph + "] /Contents 5 0 R /Resources << /XObject << /Im0 4 0 R >> >> >>\nendobj\n";
      objs[4] = "4 0 obj\n<< /Type /XObject /Subtype /Image /Width " + imgW + " /Height " + imgH + " /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length " + jpegLen + " >>\nstream\n";
      let streamEnd = "\nendstream\nendobj\n";
      let contentStream = "q " + pw + " 0 0 " + ph + " 0 0 cm /Im0 Do Q";
      objs[5] = "5 0 obj\n<< /Length " + contentStream.length + " >>\nstream\n" + contentStream + "\nendstream\nendobj\n";
      // Calculate offsets and build xref
      let header = "%PDF-1.4\n";
      let parts = [header];
      let offsets = [];
      let pos = header.length;
      for (let i = 1; i <= 5; i++) {
        if (i === 4) {
          offsets[i] = pos;
          parts.push(objs[4]);
          pos += objs[4].length;
          // jpeg binary will be inserted separately
        } else {
          offsets[i] = pos;
          parts.push(objs[i]);
          pos += objs[i].length;
        }
      }
      // Assemble as Uint8Array (text + binary jpeg + text)
      let preJpeg = parts.slice(0, 5).join(""); // header + obj1-3 + obj4 header
      let postJpeg = streamEnd + objs[5];
      let totalLen = preJpeg.length + jpegLen + postJpeg.length;
      // xref
      let xref = "xref\n0 6\n0000000000 65535 f \n";
      // Recalculate offsets with binary jpeg inserted
      let actualOffsets = [];
      let runPos = header.length;
      for (let i = 1; i <= 3; i++) { actualOffsets[i] = runPos; runPos += objs[i].length; }
      actualOffsets[4] = runPos;
      runPos += objs[4].length + jpegLen + streamEnd.length;
      actualOffsets[5] = runPos;
      for (let i = 1; i <= 5; i++) xref += String(actualOffsets[i]).padStart(10, "0") + " 00000 n \n";
      xref += "trailer\n<< /Size 6 /Root 1 0 R >>\nstartxref\n" + (totalLen) + "\n%%EOF";
      // Build final binary
      let finalBuf = new Uint8Array(totalLen + xref.length);
      let idx = 0;
      for (let i = 0; i < preJpeg.length; i++) finalBuf[idx++] = preJpeg.charCodeAt(i);
      for (let i = 0; i < jpegLen; i++) finalBuf[idx++] = jpegBytes.charCodeAt(i);
      for (let i = 0; i < postJpeg.length; i++) finalBuf[idx++] = postJpeg.charCodeAt(i);
      for (let i = 0; i < xref.length; i++) finalBuf[idx++] = xref.charCodeAt(i);
      return finalBuf;
    },

    // ==========================================
    // Fingerprint Capture (RD Service)
    // ==========================================
    captureFingerprint(callback) {
      const vm = this;
      var xhr = new XMLHttpRequest();
      xhr.open("CAPTURE", RD_SERVICE_URL, true);
      xhr.responseType = "text";
      xhr.timeout = 30000;
      xhr.onload = function () {
        if (xhr.status === 200) {
          try {
            var convert = require("xml-js");
            var json = JSON.parse(convert.xml2json(xhr.response, { compact: true }));
            let errInfo = json?.PidData?.Resp?._attributes?.errInfo;
            if (errInfo !== "Success.") {
              vm.$alert.show(errInfo || "Fingerprint capture failed.");
              callback(errInfo);
              return;
            }
            let bioData = btoa(xhr.response);
            callback(null, bioData);
          } catch (e) {
            vm.$alert.show("Failed to parse fingerprint data.");
            callback(e);
          }
        } else {
          vm.$alert.show("Fingerprint device returned error: " + xhr.status);
          callback(xhr.status);
        }
      };
      xhr.onerror = function () {
        vm.$alert.show("Could not connect to fingerprint device. Please ensure the RD Service is running.");
        callback("connection_error");
      };
      xhr.ontimeout = function () {
        vm.$alert.show("Fingerprint capture timed out. Please try again.");
        callback("timeout");
      };
      xhr.send(FINGERPRINT_PID_OPTIONS);
    },

    // Webcam methods removed - replaced by QR-based face auth (startFaceAuth, etc.)

    onModalHidden() {
      this.cancelFaceAuth();
      clearInterval(this.resendInterval);
      clearInterval(this.verifyResendInterval);
      clearInterval(this.commResendInterval);
      clearInterval(this.verifyOtpExpiryInterval);
    },

    // ==========================================
    // Helpers
    // ==========================================
    getProfilePhoto(profile) {
      let photo = profile.photo || profile.profilePhoto || "";
      if (photo && !photo.startsWith("data:")) {
        return "data:image/jpeg;base64," + photo;
      }
      return photo;
    },

    // ==========================================
    // Apply ABHA Profile to Recipient (GAP-13 uniqueness + GAP-11 locked fields)
    // ==========================================
    async applyAbhaProfile() {
      if (!this.abhaProfile) return;
      let profile = this.abhaProfile;
      let abhaNumber = profile.ABHANumber || profile.abhaNumber || profile.ResolvedAbhaNumber || "";

      // GAP-13: Check ABHA uniqueness before linking
      if (abhaNumber) {
        this.linkLoading = true;
        try {
          let { data } = await abdmService.checkAbhaUnique({ abha_number: abhaNumber });
          if (data.status && data.is_unique === false) {
            this.$alert.show(
              "This ABHA number is already linked to recipient " +
                (data.linked_recipient_name || data.linked_recipient_id || "") +
                " in the system. Cannot link to a new patient."
            );
            this.linkLoading = false;
            return;
          }
        } catch (err) {
          // If uniqueness check fails, proceed (endpoint may not exist yet)
          console.warn("ABHA uniqueness check failed:", err.message);
        } finally {
          this.linkLoading = false;
        }
      }

      // Map ABDM gender (M/F/O) to form gender (1=Male, 2=Female)
      let genderMap = { M: "1", F: "2", Male: "1", Female: "2" };
      let mappedGender = genderMap[profile.gender] || "";

      // Parse DOB
      let dob = "";
      if (profile.dob) {
        let parts = profile.dob.split("-");
        if (parts.length === 3 && parts[0].length === 2) {
          dob = parts[2] + "-" + parts[1] + "-" + parts[0];
        } else {
          dob = profile.dob;
        }
      } else if (profile.yearOfBirth && profile.monthOfBirth && profile.dayOfBirth) {
        let y = profile.yearOfBirth;
        let m = String(profile.monthOfBirth).padStart(2, "0");
        let d = String(profile.dayOfBirth).padStart(2, "0");
        dob = y + "-" + m + "-" + d;
      }

      let fullName = [profile.firstName, profile.middleName, profile.lastName].filter(Boolean).join(" ");

      let addressLine = "";
      let pincode = "";
      let district = "";
      let state = "";
      if (typeof profile.address === "string") {
        addressLine = profile.address;
      } else if (profile.address && typeof profile.address === "object") {
        addressLine = [profile.address.line, profile.address.district, profile.address.state].filter(Boolean).join(", ");
        pincode = profile.address.pincode || "";
        district = profile.address.district || "";
        state = profile.address.state || "";
      }
      pincode = profile.pincode || pincode;
      district = profile.districtName || district;
      state = profile.stateName || state;

      let photo = profile.photo || profile.profilePhoto || "";
      if (photo && !photo.startsWith("data:")) {
        photo = "data:image/jpeg;base64," + photo;
      }

      let doc = {
        abha_number: abhaNumber,
        abha_address: this.resolvedAbhaAddress || profile.preferredAddress || profile.preferredAbhaAddress || "",
        abha_verified: true,
        // GAP-11: Mark fields as locked from ABHA
        abha_locked_fields: true,
        // Profile details for auto-fill
        first_name: fullName,
        last_name: profile.lastName || "",
        DOB: dob,
        gender: mappedGender,
        phone_number_1: profile.mobile || "",
        email_id: profile.email || "",
        address_line: addressLine,
        pincode: pincode,
        district: district,
        state: state,
        abha_photo: photo,
      };
      this.$emit("onAbhaVerified", doc);
      this.createDialog = false;
      this.verifyDialog = false;
    },
  },
  beforeDestroy() {
    this.cancelFaceAuth();
    clearInterval(this.resendInterval);
    clearInterval(this.verifyResendInterval);
    clearInterval(this.commResendInterval);
    clearInterval(this.verifyOtpExpiryInterval);
  },
};
</script>

<style></style>
